// Pieces shared by the catalogue and supplier screens (Sprint 4): the words of every vocabulary the screens show
// (packages/contracts/src/catalogue.ts, purchasing.ts — spelled as the use cases spell them), the pickers built on the shared
// combobox (a supplier, a product, a brand), and the figures a branch shows beside a product (BR-CATALOGUE-026). Nothing here
// invents a class: the markup is the prototype's.
import {
  LENS_COLOUR_LABELS, PRODUCT_CATEGORY_LABELS, SUPPLIER_REFERENCE_KIND_LABELS, SUPPLIER_TYPE_LABELS, TAB_OF_CATEGORY, bp, cents,
  type BarcodeKind, type FrameGenderAgeGroup, type FrameType, type LensDesignType, type ProductBranchFigures, type ProductCategory, type ProductSummary,
  type ProductTab, type SerialStatus, type ServiceCombinationMemberRole, type ServiceCombinationRuleType, type ServiceCombinationSeverity, type SessionView, type SupplierListItem,
  type SupplierReferenceKind, type SupplierStatusFilter, type SupplierType, type VatTreatment,
} from "@visionx/contracts";
import { netOfDiscount } from "@visionx/money";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { products } from "../api/endpoints.catalogue";
import { suppliers } from "../api/endpoints.purchasing";
import { describeError, isApiError } from "../api/problem";
import { formatMoney, percentFromBp } from "../lib/format";
import { Chip } from "../ui/bits";
import { Typeahead } from "../ui/typeahead";

// --- a product's supplier price (BR-CATALOGUE-026; DVN's answers of 28 Oct 2026, change 9) ----------------------------------

export interface SupplierDiscount { name: string; bp: number }

/**
 * The supplier's default discount, from the supplier master (`GET /suppliers/{id}`, which every role reads), asked only while it
 * matters — a supplier price held or typed (BR-PURCHASING-007: the discount the supplier price is netted with as it is read). Null
 * until read, and when the read fails: the words then name no discount. Null too while it does not matter, though another screen
 * has read the supplier: a disabled query still gives what the cache holds (the check of change 9's fixes, 2 Nov 2026, C9V2-1).
 */
export function useSupplierDiscount(supplierId: string | null | undefined, enabled: boolean): SupplierDiscount | null {
  const q = useQuery({ queryKey: ["supplier", supplierId ?? ""], queryFn: () => suppliers.get(supplierId!), enabled: enabled && !!supplierId, staleTime: 60_000, retry: false });
  const v = q.data?.data;
  return enabled && v ? { name: v.name, bp: v.defaultDiscountBp } : null;
}

/** A supplier price net of its supplier's discount (BR-PRICING-020), by the money package alone (`netOfDiscount`, TC-MONEY-021). */
export const supplierPriceNet = (priceCents: number, discountBp: number): number => netOfDiscount(cents(priceCents), bp(discountBp));

const SUPPLIER_PRICE_WHAT = "What the supplier charges for one, before any discount";

/**
 * The form's help under Supplier price (UC-CATALOGUE-001 A14 step 1): what it is for, as built — the cost an opening count takes,
 * and a made-to-order lens's on Products, where no supplier price list gives one; for a service or a composite (`costed` false),
 * never counted at an opening nor costed so, the first clause alone (the review of change 9, 1 Nov 2026, C9R-PWA-8) — and, where
 * the supplier has a discount, that it comes off it, with the net of the amount typed.
 */
export function supplierPriceHelp(discount: SupplierDiscount | null, typedCents: number | null, costed = true): string {
  if (!discount || discount.bp === 0) return costed ? `${SUPPLIER_PRICE_WHAT}: the cost an opening count takes, and a made-to-order lens's on Products, where no supplier price list gives one` : SUPPLIER_PRICE_WHAT;
  const net = typedCents === null ? "" : `: ${formatMoney(supplierPriceNet(typedCents, discount.bp))} net`;
  return `What the supplier charges for one, before ${discount.name}'s ${percentFromBp(discount.bp)} discount, which comes off it${net}`;
}

/** The panel's Supplier price (UC-CATALOGUE-003 A14): the price ex VAT, and its net where the supplier has a discount, each amount in the money face. */
export function supplierPriceWords(priceCents: number, discount: SupplierDiscount | null): ReactNode {
  const net = discount && discount.bp > 0
    ? <> · <span className="mono">{formatMoney(supplierPriceNet(priceCents, discount.bp))}</span> net of {discount.name}'s {percentFromBp(discount.bp)} discount</>
    : null;
  return <><span className="mono">{formatMoney(priceCents)}</span> ex VAT{net}</>;
}

/** A cost's source in words (UC-CATALOGUE-003 A14 step 2): a list's line is the lab price, else the product's supplier price. */
export const COST_SOURCE_WORD: Readonly<Record<NonNullable<ProductBranchFigures["costSource"]>, string>> = { PriceList: "lab price", SupplierPrice: "supplier price" };

/**
 * The panel's word beside a branch's cost, from the same read as the amount (the figures' `costSource`; the review of change 9, 1 Nov
 * 2026, C9R-PWA-1, C9R-CONTRACT-1): a made-to-order lens's leg, "lab price" or "supplier price"; a stock line's average "wtd avg";
 * a made-to-order lens whose figures name no source, none.
 */
export function costWordOf(p: Pick<ProductSummary, "category" | "madeToOrder">, f: Pick<ProductBranchFigures, "costSource">): string | null {
  if (f.costSource) return COST_SOURCE_WORD[f.costSource];
  return pricedOnLabPrice(p) ? null : "wtd avg";
}

// --- vocabularies ---------------------------------------------------------------------------------------------------------

export type ListTab = "all" | ProductTab;
/** The tabs of Products in their order (BR-CATALOGUE-024): the two lens categories share Lenses. */
export const LIST_TABS: readonly [ListTab, string][] = [["all", "All"], ["frames", "Frames"], ["lenses", "Lenses"], ["contactLenses", "Contact lenses"], ["accessories", "Accessories"], ["services", "Services"]];
export const TAB_LABEL: Readonly<Record<ListTab, string>> = Object.fromEntries(LIST_TABS) as Record<ListTab, string>;
/** The categories a tab lists. */
export const TAB_CATEGORIES: Readonly<Record<ProductTab, readonly ProductCategory[]>> = {
  frames: ["Frame"], lenses: ["PlasticLens", "GlassLens"], contactLenses: ["ContactLens"], accessories: ["Accessory"], services: ["Service"],
};
export const categoryLabel = (c: ProductCategory): string => PRODUCT_CATEGORY_LABELS[c];
/** The list's Category column names the tab, as `products.html` draws it ("Frames", "Lenses"). */
export const tabLabelOf = (c: ProductCategory): string => TAB_LABEL[TAB_OF_CATEGORY[c]];
export const isLens = (c: ProductCategory): boolean => c === "PlasticLens" || c === "GlassLens";

export const VAT_LABEL: Readonly<Record<VatTreatment, string>> = { Standard: "Standard", ZeroRated: "Zero-rated", Exempt: "Exempt" };
export const FRAME_TYPE_LABEL: Readonly<Record<FrameType, string>> = { Normal: "Normal (full-rim)", Nylon: "Nylon (semi-rimless, grooved)", Rimless: "Rimless (drilled)" };
export const FRAME_TYPE_SHORT: Readonly<Record<FrameType, string>> = { Normal: "Normal", Nylon: "Nylon", Rimless: "Rimless" };
export const GENDER_AGE_LABEL: Readonly<Record<FrameGenderAgeGroup, string>> = { Men: "Men", Women: "Women", Unisex: "Unisex", Kids: "Kids" };
export const LENS_DESIGN_LABEL: Readonly<Record<LensDesignType, string>> = { SingleVision: "Single vision", Bifocal: "Bifocal", Trifocal: "Trifocal", Progressive: "Progressive", Occupational: "Occupational" };
export const LENS_COLOUR_LABEL = LENS_COLOUR_LABELS;
export const BARCODE_KIND_LABEL: Readonly<Record<BarcodeKind, string>> = { SupplierEAN: "Supplier EAN", SupplierUPC: "Supplier UPC", Generated: "Practice barcode", Alternate: "Alternate code" };
export const RULE_TYPE_LABEL: Readonly<Record<ServiceCombinationRuleType, string>> = {
  MutuallyExclusive: "Mutually exclusive", Requires: "Requires", MaxQuantityPerOrder: "Maximum quantity per order", OncePerPatientPerNDays: "Once per patient per N days",
};
export const SEVERITY_LABEL: Readonly<Record<ServiceCombinationSeverity, string>> = { Block: "Block", Warn: "Warn" };
export const MEMBER_ROLE_LABEL: Readonly<Record<ServiceCombinationMemberRole, string>> = { Member: "Member", Trigger: "Trigger", Required: "Required" };
/** A serial's status on the catalogue's screens: an Available frame is shown In store (UC-CATALOGUE-004 step 3). */
export const SERIAL_WORD: Readonly<Record<SerialStatus, string>> = {
  Available: "In store", Reserved: "Reserved", AtLab: "At lab", Sold: "Sold", InTransit: "In transit", Damaged: "Damaged", WrittenOff: "Written off", Returned: "Returned",
};
export const SUPPLIER_TYPE_LABEL = SUPPLIER_TYPE_LABELS;
export const supplierTypesText = (types: readonly SupplierType[]): string => types.map((t) => SUPPLIER_TYPE_LABELS[t]).join(" · ");

/** BR-CATALOGUE-021: what keeps a product from being deleted, in words (UC-CATALOGUE-006 E1). */
const PRODUCT_REFERENCE_LABEL: Readonly<Record<string, string>> = {
  serial: "serials", stock_movement: "stock movements", stock_take: "a stock take in progress", stock_transfer: "a transfer not yet received",
  goods_receipt: "goods receipts", import_row: "price-list import rows", composite_component: "a composite that includes it",
  service_combination_rule: "combination rules", lens_component_option: "lens options restricted to it",
};

/** "a, b and c". */
export function andList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/**
 * A write's refusal in words, where no field of the form holds it (D2-3): a 403 names the permission, a 429 the wait, a reused
 * key and a request still in flight say what to do; a 500 keeps its request id for support. Never an API path.
 */
export function refusalWords(error: unknown): string {
  if (isApiError(error)) {
    if (error.code === "PERMISSION_DENIED") return `Your role may not make this change${typeof error.meta.permission === "string" ? ` — it needs ${error.meta.permission}` : ""}.`;
    if (error.code === "RATE_LIMITED") {
      const wait = Number(error.meta.retryAfterSeconds);
      return `Too many requests — wait ${Number.isFinite(wait) && wait > 0 ? `${wait} second${wait === 1 ? "" : "s"}` : "a moment"} and try again.`;
    }
    if (error.code === "IDEMPOTENCY_KEY_REUSED") return "An earlier save from this form was received with other details — close it and look at the list before saving again.";
    if (error.code === "REQUEST_IN_FLIGHT") return "The same save is still being processed — wait a moment, then try again.";
    if (error.code === "VALIDATION_FAILED" && error.errors.length > 0) return error.errors.map((f) => f.message).join(" · ");
  }
  return describeError(error);
}

/** The kinds a `has_history` refusal names, in words; null when the error is not that refusal. */
export function referenceRefusal(error: unknown, labels: Readonly<Record<string, string>>): string | null {
  if (!isApiError(error) || error.code !== "IMMUTABLE_RECORD" || error.meta.reason !== "has_history" || !Array.isArray(error.meta.blockingRecordTypes)) return null;
  return andList((error.meta.blockingRecordTypes as string[]).map((k) => labels[k] ?? k.replace(/_/g, " ")));
}
export const productReferenceRefusal = (error: unknown): string | null => referenceRefusal(error, PRODUCT_REFERENCE_LABEL);
export const supplierReferenceRefusal = (error: unknown): string | null => referenceRefusal(error, SUPPLIER_REFERENCE_KIND_LABELS as Readonly<Record<SupplierReferenceKind, string>>);

// --- a branch's figures beside a product (BR-CATALOGUE-026) -------------------------------------------------------------

const dash = <span className="faint">—</span>;

/** A made-to-order lens is priced from its lab's list: its markup is on the lab price (FIXTURE §5). */
export const pricedOnLabPrice = (p: Pick<ProductSummary, "category" | "madeToOrder">): boolean => isLens(p.category) && p.madeToOrder;

/**
 * The markup cell: the band's or the product's percentage — a branch's own band or markup marked "Menlyn override", as
 * `products.html` draws the Lindberg's 60% (`markupScope`, pricing's figures) — and a lens's `+90%` on the lab price; none for a
 * service.
 */
export function markupCell(p: Pick<ProductSummary, "category" | "madeToOrder">, f: ProductBranchFigures | null, branchName?: string | null): ReactNode {
  if (p.category === "Service" || !f || f.markupBp === null) return dash;
  if (pricedOnLabPrice(p)) return <>+{percentFromBp(f.markupBp)}<div className="small faint nw">on lab price</div></>;
  // Free to wrap ("Menlyn" over "override") when the columns are squeezed: it must not widen the column past "on lab price".
  if (f.markupScope === "Branch") return <>{percentFromBp(f.markupBp)}<div className="small faint">{branchName ? `${branchName} override` : "branch override"}</div></>;
  return percentFromBp(f.markupBp);
}

/** The acting branch's name, as the session names it (the page heads' "Catalogue for Menlyn"). */
export function activeBranchName(s: SessionView | null): string | null {
  if (!s) return null;
  return s.switchableBranches.find((b) => b.id === s.activeBranchId)?.name ?? s.assignments.find((a) => a.branchId === s.activeBranchId)?.branchName ?? null;
}

/** Where the units a branch holds are, beside the on-hand figure: `2 in store, 1 at lab`; nothing while all are in store. */
export function stockBreakdown(s: NonNullable<ProductBranchFigures["stock"]>): string | null {
  const away = [[s.reserved, "reserved"], [s.atLab, "at lab"], [s.damaged, "damaged"], [s.inTransit, "in transit"]] as const;
  if (away.every(([n]) => n === 0)) return null;
  return [s.inStore > 0 ? `${s.inStore} in store` : null, ...away.filter(([n]) => n > 0).map(([n, w]) => `${n} ${w}`)].filter(Boolean).join(", ");
}

/** The stock cell: a service holds none; a made-to-order product says so; a composite holds none of its own. */
export function stockCell(p: Pick<ProductSummary, "category" | "madeToOrder" | "isComposite">, f: ProductBranchFigures | null): ReactNode {
  if (p.category === "Service" || p.isComposite) return dash;
  if (p.madeToOrder) return <span className="faint">Made to order</span>;
  if (!f?.stock) return dash;
  const more = stockBreakdown(f.stock);
  return <><div className="num">{f.stock.onHand}</div>{more && <div className="small faint nw breakdown">{more}</div>}</>;
}

export function InactiveChip() { return <Chip tone="neutral">Inactive</Chip>; }

// --- pickers ---------------------------------------------------------------------------------------------------------------

/** A supplier picked from the organisation's suppliers as the user types (UC-PURCHASING-003 A2): Active for anything new. */
export function SupplierPicker({ value, onPick, status = "Active", type, label, ariaLabel, error, help, required, bare, boxClass, placeholder, id, disabled }: {
  value: { id: string; name: string } | null; onPick: (s: SupplierListItem | null) => void; status?: SupplierStatusFilter | undefined; type?: SupplierType | undefined;
  label?: ReactNode; ariaLabel?: string | undefined; error?: string | undefined; help?: ReactNode; required?: boolean | undefined; bare?: boolean | undefined; boxClass?: string | undefined;
  placeholder?: string | undefined; id?: string | undefined;
  /** Shows the supplier with no way to change or clear it — a lens its supplier's list hid (UC-CATALOGUE-002 E8). */
  disabled?: boolean | undefined;
}) {
  return (
    <Typeahead<SupplierListItem>
      id={id} label={label} ariaLabel={ariaLabel} error={error} help={help} required={required} bare={bare} boxClass={boxClass} placeholder={placeholder ?? "Type a supplier's name"} disabled={disabled}
      value={value as SupplierListItem | null} labelOf={(s) => s.name} onPick={onPick} keyOf={(s) => s.id}
      source={{ kind: "remote", key: ["suppliers", "picker", status, type ?? ""], search: async (q) => (await suppliers.list({ q, status, type }, { limit: 20 })).data.items, minChars: 1 }}
      renderOption={(s) => <div className="grow"><div className="primary">{s.name}{s.status === "Inactive" ? " · inactive" : ""}</div><div className="sub">{supplierTypesText(s.types)}</div></div>}
      emptyText={(q) => `No supplier matches “${q}”.`}
    />
  );
}

/** How many products a picker reads for what is typed, before it leaves out what it may not offer and shows the first ten. */
const PICKER_READ = 50;

/**
 * A product picked from the catalogue (UC-CATALOGUE-003 A5): two typed characters, up to ten active single products of the allowed
 * categories — of one supplier where `supplierId` says so — and of those, the ones `allow` admits as the list is shown, so a
 * product another row picked since is not offered again (TC-CATALOGUE-132).
 */
export function ProductPicker({ value, onPick, categories, supplierId, allow, label, ariaLabel, error, help, required, bare, boxClass, id, keyTag }: {
  value: ProductSummary | null; onPick: (p: ProductSummary | null) => void; categories?: readonly ProductCategory[] | undefined; supplierId?: string | undefined; allow?: ((p: ProductSummary) => boolean) | undefined;
  label?: ReactNode; ariaLabel?: string | undefined; error?: string | undefined; help?: ReactNode; required?: boolean | undefined; bare?: boolean | undefined; boxClass?: string | undefined;
  id?: string | undefined; keyTag?: string | undefined;
}) {
  return (
    <Typeahead<ProductSummary>
      id={id} label={label} ariaLabel={ariaLabel} error={error} help={help} required={required} bare={bare} boxClass={boxClass} placeholder="SKU or name"
      value={value} labelOf={(p) => `${p.sku} ${p.displayName}`} onPick={onPick} keyOf={(p) => p.id} filter={allow} max={10}
      source={{
        kind: "remote", key: ["products", "picker", categories?.join(",") ?? "", supplierId ?? "", keyTag ?? ""], minChars: 2,
        // Read more than are shown, then leave out the inactive and the composites, so a match past them is not lost.
        search: async (q) => (await products.list({ q, category: categories, supplierId }, { limit: PICKER_READ })).data.items.filter((p) => p.status === "Active" && !p.isComposite),
      }}
      renderOption={(p) => <div className="grow"><div className="primary"><span className="mono">{p.sku}</span> {p.displayName}</div>{p.supplier && <div className="sub">{p.supplier.name}</div>}</div>}
      emptyText={(q) => `No active product matches “${q}”.`}
    />
  );
}

/** The organisation's brands as the user types (UC-CATALOGUE-003 A2): `Lin` → Lindberg. */
export function BrandPicker({ value, onPick, ariaLabel, boxClass }: { value: string | null; onPick: (b: string | null) => void; ariaLabel: string; boxClass?: string | undefined }) {
  return (
    <Typeahead<string>
      bare ariaLabel={ariaLabel} boxClass={boxClass} placeholder="Brand · All"
      value={value} labelOf={(b) => b} onPick={onPick} keyOf={(b) => b}
      source={{ kind: "remote", key: ["products", "brands"], search: async (q) => (await products.brands(q)).data.items, minChars: 1 }}
      renderOption={(b) => <span className="primary">{b}</span>} emptyText={(q) => `No brand starts “${q}”.`}
    />
  );
}
